import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { sitesQuery, type SiteFilters } from "@/api/queries";
import { hasRole, useAuth } from "@/auth/auth-context";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { ErrorBlock, LoadingBlock, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { DataTable, EmptyRow, Td, Th } from "@/components/ui/table";
import { OrganizationFilter } from "@/features/organizations/organization-filter";
import { useApiClient } from "@/lib/api-context";
import { useListFilters } from "@/lib/use-list-filters";
import { EM_DASH, formatDateTime } from "@/lib/format";
import { SiteCreateDialog } from "./site-create-dialog";

export function SitesPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const client = useApiClient();
  const { user } = useAuth();
  const canCreate = hasRole(user, ["ADMIN", "SUPER_ADMIN"]);
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const { value: filterValue, page, setFilter, setPage } = useListFilters();
  const [creating, setCreating] = useState(false);

  const filters: SiteFilters = {
    ...(filterValue("search") ? { search: filterValue("search") } : {}),
    ...(filterValue("organizationId") ? { organizationId: filterValue("organizationId") } : {}),
    page,
  };

  const sites = useQuery(sitesQuery(client, filters));

  // A tenant user sees a single organization, so naming it in every row would
  // be a column of the same value repeated.
  const columnCount = isSuperAdmin ? 7 : 6;

  return (
    <>
      <PageHeader
        title={t("sitesTitle")}
        description={t("sitesDescription")}
        action={canCreate ? <Button onClick={() => setCreating(true)}>{t("addSite")}</Button> : null}
      />

      <Card className="mb-4">
        <CardBody>
          <div className="grid gap-3 sm:grid-cols-2">
            <SearchInput
              value={filterValue("search")}
              placeholder={t("searchSites")}
              onChange={(value) => setFilter("search", value)}
            />

            {isSuperAdmin ? (
              <OrganizationFilter
                value={filterValue("organizationId")}
                onChange={(value) => setFilter("organizationId", value)}
              />
            ) : null}
          </div>
        </CardBody>
      </Card>

      {sites.isPending ? <LoadingBlock label={t("loadingSites")} /> : null}

      {sites.isError ? (
        <ErrorBlock error={sites.error} onRetry={() => void sites.refetch()} />
      ) : null}

      {sites.data ? (
        <Card>
          <DataTable caption="Sites">
            <thead>
              <tr>
                {isSuperAdmin ? <Th>{tCommon("organization")}</Th> : null}
                <Th>{tCommon("name")}</Th>
                <Th>{tCommon("address")}</Th>
                <Th numeric>{t("colAssets")}</Th>
                <Th numeric>{t("colDevices")}</Th>
                <Th numeric>{t("colGateways")}</Th>
                <Th>{t("colAdded")}</Th>
              </tr>
            </thead>

            <tbody>
              {sites.data.data.length === 0 ? (
                <EmptyRow colSpan={columnCount}>{t("emptySites")}</EmptyRow>
              ) : null}

              {sites.data.data.map((site) => (
                <tr key={site.id}>
                  {isSuperAdmin ? <Td>{site.organizationName}</Td> : null}
                  <Td className="font-medium">
                    <Link
                      to={`/sites/${site.id}`}
                      className="text-brand underline underline-offset-2"
                    >
                      {site.name}
                    </Link>
                  </Td>
                  <Td>{site.address ?? EM_DASH}</Td>
                  <Td numeric>{site.counts.assets}</Td>
                  <Td numeric>
                    {site.counts.devices > 0 ? (
                      // Straight to the devices list already filtered, which
                      // is the question a count in a table provokes.
                      <Link
                        to={`/devices?siteId=${site.id}`}
                        className="text-brand underline underline-offset-2"
                      >
                        {site.counts.devices}
                      </Link>
                    ) : (
                      site.counts.devices
                    )}
                  </Td>
                  <Td numeric>
                    {site.counts.gateways > 0 ? (
                      <Link
                        to={`/gateways?siteId=${site.id}`}
                        className="text-brand underline underline-offset-2"
                      >
                        {site.counts.gateways}
                      </Link>
                    ) : (
                      // A site with sensors and no gateway is worth noticing:
                      // the readings are arriving through somebody else's
                      // radio, or through one nobody wrote down.
                      site.counts.gateways
                    )}
                  </Td>
                  <Td>{formatDateTime(site.createdAt)}</Td>
                </tr>
              ))}
            </tbody>

            <tfoot>
              <tr>
                <Td colSpan={columnCount} className="border-b-0">
                  <Pagination
                    page={sites.data.pagination.page}
                    totalPages={sites.data.pagination.totalPages}
                    total={sites.data.pagination.total}
                    noun={{ one: tCommon("noun.site.one"), other: tCommon("noun.site.other") }}
                    onChange={setPage}
                  />
                </Td>
              </tr>
            </tfoot>
          </DataTable>
        </Card>
      ) : null}

      <SiteCreateDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}
